import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import db from "./Firebase";

const PAGE_SIZE = 500;

const categoryLabel = (cat) => {
    const map = {
        "001": "Content Creation: Basic Principles",
        "005": "Professional Nail Art & Extensions",
        "006": "Hair Color Basics: Trends & Techniques",
        "007": "WhatsApp Business for Small Business Owners",
    };
    return map[cat] || cat;
};

const moduleLabel = (m) =>
    m === "My Funds Page" ? "Finance" : m === "Learn Page" ? "Learn" : m;

const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    const d = ts instanceof Date ? ts : new Date(ts);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleString();
};

const InactiveVisitsPage = () => {
    const [allVisits, setAllVisits] = useState([]);
    const [lastActiveMap, setLastActiveMap] = useState({});
    const [personalDetails, setPersonalDetails] = useState({});
    const [locationMap, setLocationMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [inactivityMonths, setInactivityMonths] = useState(0); // 0 = All
    const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
    const [searchUser, setSearchUser] = useState("");

    useEffect(() => {
        getDocs(collection(db, "profile_type")).then((snap) => {
            const map = {};
            snap.forEach((d) => {
                const data = d.data();
                map[data.phone_no] = {
                    name: data.name || "N/A",
                    profile_type: data.profile_type || "N/A",
                    age_range: data.age_range || "N/A",
                };
            });
            setPersonalDetails(map);
        });
    }, []);

    useEffect(() => {
        getDocs(collection(db, "parlour_name")).then((snap) => {
            const map = {};
            snap.forEach((d) => {
                const data = d.data();
                map[data.phone] = { city: data.city || "N/A", state: data.state || "N/A" };
            });
            setLocationMap(map);
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        const unsubscribe = onSnapshot(collection(db, "users_activity"), (snapshot) => {
            const visits = [];
            const activeMap = {};

            snapshot.docs.forEach((userDoc) => {
                const userId = userDoc.id;
                const userData = userDoc.data();

                if (userData.last_active) {
                    const la = userData.last_active?.toDate
                        ? userData.last_active.toDate()
                        : new Date(userData.last_active);
                    if (!isNaN(la.getTime())) activeMap[userId] = la;
                }

                if (Array.isArray(userData.page_visits)) {
                    userData.page_visits.forEach((visit) => {
                        let visitTime = visit.visit_time;
                        if (visitTime?.toDate) visitTime = visitTime.toDate();
                        visits.push({
                            docid: userId,
                            page_name: visit.page_name,
                            visit_time: visitTime,
                            category: visit.category,
                            description: visit.description,
                        });
                    });
                }
            });

            visits.sort((a, b) => (b.visit_time || 0) - (a.visit_time || 0));
            setAllVisits(visits);
            setLastActiveMap(activeMap);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const getInactiveDays = (userId) => {
        const la = lastActiveMap[userId];
        if (!la) return 9999;
        return Math.floor((new Date() - la) / 86400000);
    };

    const getInactiveLabel = (userId) => {
        const days = getInactiveDays(userId);
        if (days >= 9999) return "Unknown";
        if (days < 7) return `${days}d ago`;
        if (days < 30) return `${Math.floor(days / 7)}w ago`;
        const m = Math.floor(days / 30);
        return `${m} month${m > 1 ? "s" : ""} ago`;
    };

    const inactivityColor = (userId) => {
        const days = getInactiveDays(userId);
        if (days < 30) return "#198754";
        if (days < 60) return "#fd7e14";
        if (days < 90) return "#dc3545";
        return "#6f42c1";
    };

    const filteredVisits = allVisits.filter((visit) => {
        if (!visit.docid || visit.docid === "null" || !/^\d{10}$/.test(visit.docid)) return false;
        if (inactivityMonths !== 0) {
            const days = getInactiveDays(visit.docid);
            if (inactivityMonths === 6) {
                if (days < 180) return false; // 6M+ = 180+ days
            } else {
                const minDays = inactivityMonths * 30;
                const maxDays = (inactivityMonths + 1) * 30;
                if (days < minDays || days >= maxDays) return false;
            }
        }
        if (searchUser) {
            const name = (personalDetails[visit.docid]?.name || "").toLowerCase();
            return visit.docid.includes(searchUser) || name.includes(searchUser.toLowerCase());
        }
        return true;
    });

    const displayedVisits = filteredVisits.slice(0, displayCount);

    return (
        <>
            <div className="p-2 flex justify-between">
                <Link to="/">
                    <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
                </Link>
            </div>

            <div className="card-header pb-0 px-3 d-flex justify-content-end">
                <Link to="/page_visit">
                    <button className="btn btn-success btn-sm me-2">Page Visits</button>
                </Link>
                <Link to="/analytics_details">
                    <button className="btn btn-success btn-sm">User Analytics</button>
                </Link>
            </div>

            <div className="p-4">
                <h4 className="font-bold mb-3">Inactive Users — Page Visits</h4>

                {/* Filters */}
                <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                        <span className="me-2" style={{ fontSize: "13px", fontWeight: 600 }}>Inactive since:</span>
                        <button
                            className={`btn btn-sm ${inactivityMonths === 0 ? "btn-dark" : "btn-outline-dark"}`}
                            onClick={() => { setInactivityMonths(0); setDisplayCount(PAGE_SIZE); }}
                        >
                            All
                        </button>
                        {[1, 2, 3, 4, 5].map((m) => (
                            <button
                                key={m}
                                className={`btn btn-sm ${inactivityMonths === m ? "btn-dark" : "btn-outline-dark"}`}
                                onClick={() => { setInactivityMonths(m); setDisplayCount(PAGE_SIZE); }}
                            >
                                {m}M
                            </button>
                        ))}
                        <button
                            className={`btn btn-sm ${inactivityMonths === 6 ? "btn-dark" : "btn-outline-dark"}`}
                            onClick={() => { setInactivityMonths(6); setDisplayCount(PAGE_SIZE); }}
                        >
                            6M+
                        </button>
                    </div>

                    <input
                        type="text"
                        placeholder="Search by User ID or Name…"
                        value={searchUser}
                        onChange={(e) => { setSearchUser(e.target.value); setDisplayCount(PAGE_SIZE); }}
                        className="border p-2 rounded-md"
                        style={{ width: "240px" }}
                    />

                    <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {loading ? "Loading…" : `${displayedVisits.length} / ${filteredVisits.length} visits shown`}
                    </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300 text-sm">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="border p-2 text-left">#</th>
                                <th className="border p-2 text-left">User ID</th>
                                <th className="border p-2 text-left">Name</th>
                                <th className="border p-2 text-left">City</th>
                                <th className="border p-2 text-left">State</th>
                                <th className="border p-2 text-left">Profile Type</th>
                                <th className="border p-2 text-left">Age Range</th>
                                <th className="border p-2 text-left">Module</th>
                                <th className="border p-2 text-left">Category</th>
                                <th className="border p-2 text-left">Page</th>
                                <th className="border p-2 text-left">Visit Time</th>
                                <th className="border p-2 text-left">Last Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="12" className="text-center p-5">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                        <span className="text-muted">Loading data…</span>
                                    </td>
                                </tr>
                            ) : displayedVisits.length === 0 ? (
                                <tr>
                                    <td colSpan="12" className="text-center text-gray-500 p-4">No records found</td>
                                </tr>
                            ) : (
                                displayedVisits.map((visit, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="border p-2">{idx + 1}</td>
                                        <td className="border p-2">{visit.docid}</td>
                                        <td className="border p-2">{personalDetails[visit.docid]?.name || "N/A"}</td>
                                        <td className="border p-2">{locationMap[visit.docid]?.city || "N/A"}</td>
                                        <td className="border p-2">{locationMap[visit.docid]?.state || "N/A"}</td>
                                        <td className="border p-2">{personalDetails[visit.docid]?.profile_type || "N/A"}</td>
                                        <td className="border p-2">{personalDetails[visit.docid]?.age_range || "N/A"}</td>
                                        <td className="border p-2">{moduleLabel(visit.page_name)}</td>
                                        <td className="border p-2">{visit.description}</td>
                                        <td className="border p-2 whitespace-normal break-words" style={{ maxWidth: "200px" }}>
                                            {categoryLabel(visit.category)}
                                        </td>
                                        <td className="border p-2" style={{ whiteSpace: "nowrap" }}>
                                            {formatTimestamp(visit.visit_time)}
                                        </td>
                                        <td className="border p-2" style={{ whiteSpace: "nowrap", fontSize: "12px", fontWeight: 600, color: inactivityColor(visit.docid) }}>
                                            {getInactiveLabel(visit.docid)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Load More */}
                {!loading && filteredVisits.length > displayCount && (
                    <div className="text-center mt-4">
                        <button
                            className="btn btn-outline-primary px-5"
                            onClick={() => setDisplayCount((c) => c + PAGE_SIZE)}
                        >
                            Load More{" "}
                            <span className="badge bg-primary ms-1">
                                {filteredVisits.length - displayCount} remaining
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default InactiveVisitsPage;
